import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useArtEditorContext } from '../../context';
import TextSection from './sections/TextSection';
import ShapeSection from './sections/ShapeSection';
import ImageSection from './sections/ImageSection';
import IconSection from './sections/IconSection';
import VideoSection from './sections/VideoSection';
import PageSection from './sections/PageSection';
import LayerActions from './sections/LayerActions';
import { documentColors } from '../../../model/colors';
import { photoFrameOf } from '../../../model/operations';
import { useEnter } from '../../motion';
import './styles.scss';

// The brand kit's avatar (2.10.0, the profile's Instagram picture) as an image source.
export const avatarSource = brandKit => (brandKit?.avatar
  ? { kind: 'asset', asset_id: brandKit.avatar.asset_id, url: brandKit.avatar.url, width: brandKit.avatar.width, height: brandKit.avatar.height } : null);

// "Selected element options": a floating bar under the art whose controls follow the
// selection (text, shape, image, icon, or the page when nothing is selected). Swapping
// sections fades the new ones in; editing values never re-animates.
export default function Inspector({ onAskAI }) {
  const { t } = useTranslation('artEditor');
  const { editor, brandKit, readOnly, uploadImages, trimVideo } = useArtEditorContext();
  const doc = editor.document;
  const layer = editor.selected.length === 1 ? editor.selected[0] : null;
  const colors = useMemo(() => ({
    brand: (brandKit?.colors || []).map(color => color.hex),
    brandFonts: [brandKit?.fonts?.heading?.family, brandKit?.fonts?.body?.family].filter(Boolean),
    used: documentColors(doc, 9),
  }), [brandKit, doc]);
  const ref = useEnter(layer ? `${layer.type}-${layer.type === 'shape' ? layer.shape : ''}` : 'page', { y: 6 }, { duration: 0.16 });
  if (!doc || !editor.page) return null;
  const update = (patch, meta) => editor.update(layer.id, patch, meta);
  // What a photo paint offers in the picker: send one, pick one in the gallery, move it
  // inside its mask on the stage. On a photo's shade they act on the frame under it (the
  // photo panel resolves the selection the same way).
  const frame = layer ? photoFrameOf(editor.page, layer) : null;
  const photo = layer ? { onUpload: files => uploadImages(files, { target: frame }), onGallery: () => editor.setTool('photos', { mode: 'fill', force: true }),
    onMove: () => editor.crop(frame.id) } : null;
  return <div className="art-inspector" role="toolbar" aria-label={t('inspector')} aria-disabled={readOnly || undefined}>
    <div ref={ref} className={`art-inspector__bar ${readOnly ? 'is-disabled' : ''}`} inert={readOnly ? '' : undefined}>
      {!layer && <PageSection editor={editor} colors={colors} brandKit={brandKit} />}
      {layer?.type === 'text' && <TextSection layer={layer} update={update} colors={colors} photo={photo} />}
      {layer?.type === 'shape' && <ShapeSection layer={layer} update={update} colors={colors} photo={photo} />}
      {layer?.type === 'image' && <ImageSection layer={layer} update={update} photo={photo} avatar={avatarSource(brandKit)} />}
      {layer?.type === 'icon' && <IconSection layer={layer} update={update} colors={colors} openElements={() => editor.setTool('elements', { mode: 'replaceIcon', force: true })} />}
      {layer?.type === 'video' && <VideoSection layer={layer} update={update} onTrim={trimVideo} onMove={() => editor.crop(layer.id)} />}
      {/* The AI never edits a video (2.13.0): its clips are the creator's. */}
      {layer && <LayerActions layer={layer} editor={editor} onAskAI={layer.type === 'video' ? null : onAskAI} colors={colors} />}
    </div>
  </div>;
}
